import { useMutation, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';

/**
 * Hook to create a Stripe checkout session for a client.
 *
 * This hook creates a Stripe hosted checkout session by calling the backend API.
 * The API validates the provided price IDs and creates a checkout session with
 * the client's associated Stripe customer.
 *
 * @param {Object} options - React Query mutation options
 * @param {Function} [options.onSuccess] - Called when checkout session created successfully
 *   - Receives response: { body: { sessionId, url, expiresAt } }
 *   - Typical usage: window.location.href = data.body.url
 * @param {Function} [options.onError] - Called when checkout session creation fails
 *   - Receives error object
 * @param {Function} [options.onSettled] - Called after success or error
 */
const useClientSubscriptionsCheckout = (options = {}) => {
  const firebase = useFirebase();
  const queryClient = useQueryClient();
  const { onSettled, ...rest } = options;

  return useMutation({
    mutationFn: async ({ clientId, stripePriceIds, successUrl, cancelUrl }) => {
      // Validate required parameters
      if (!clientId) {
        throw new Error('clientId is required');
      }

      if (!stripePriceIds || !Array.isArray(stripePriceIds) || stripePriceIds.length === 0) {
        throw new Error('stripePriceIds array is required and must contain at least one price ID');
      }

      // Get authenticated Firebase agent
      const agent = await firebase.getAgent();

      // Create checkout session
      const response = await agent
        .post(`/clients/${encodeURIComponent(clientId)}/subscriptions/checkout`)
        .send({
          stripePriceIds,
          ...(successUrl && { successUrl }),
          ...(cancelUrl && { cancelUrl }),
        });

      return response;
    },
    onSettled: (data, error, payload, context) => {
      // Invalidate so suspensions is re-fetched
      queryClient.invalidateQueries(['/currentUser/organizations']);
      if (onSettled) {
        onSettled(data, error, payload, context);
      }
    },
    ...rest,
  });
};

export default useClientSubscriptionsCheckout;
